import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AdminUserDetailsResponse, AdminUserPublishedBook, AdminUserPurchase } from '../../../shared/services/responses/platform/admin-user-list.response';
import {
  PUBLICATION_BOOK_TYPE_OPTIONS,
  PUBLICATION_BUDGET_OPTIONS,
  PUBLICATION_TEXT_READY_OPTIONS,
  PUBLICATION_TIMEFRAME_OPTIONS,
  publicationProfileLabel
} from '../../../shared/constants/user-publication-profile.constants';

@Component({
  selector: 'app-admin-user-details-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <h2 mat-dialog-title class="dlg-title">{{ data.user.userName }}</h2>

    <mat-dialog-content class="dlg">
      <h3>Dados cadastrais</h3>
      <div class="meta">
        <div>Email: <strong>{{ data.user.userEmail || '—' }}</strong></div>
        <div>CPF: <strong>{{ data.user.userCpf || '—' }}</strong></div>
        <div>Telefone: <strong>{{ data.user.mobilePhone || '—' }}</strong></div>
        <div>Ativo: <strong>{{ data.user.isActive ? 'Sim' : 'Não' }}</strong></div>
        <div>Cadastrado em: <strong>{{ data.user.datetimeInclusion | date:'dd/MM/yyyy HH:mm' }}</strong></div>
        <div>Endereço: <strong>{{ formatAddress() }}</strong></div>
      </div>

      <h3>Perfil de publicação</h3>
      <div class="meta">
        <div>Que livro quer publicar: <strong>{{ bookTypeLabel() }}</strong></div>
        <div>Texto pronto: <strong>{{ textReadyLabel() }}</strong></div>
        <div>Quando quer publicar: <strong>{{ timeframeLabel() }}</strong></div>
        <div>Orçamento: <strong>{{ budgetLabel() }}</strong></div>
        <div>Preenchido em:
          <strong>{{ data.user.datetimePublicationProfile ? (data.user.datetimePublicationProfile | date:'dd/MM/yyyy HH:mm') : '—' }}</strong>
        </div>
      </div>

      <h3>Livros publicados</h3>
      <div *ngFor="let b of (data.publishedBooks || []); trackBy: trackBook" class="item-row">
        <div class="item-main">
          <strong>{{ b.bookTitle || 'Sem título' }}</strong>
          <span class="muted">{{ b.authorName || '—' }}</span>
        </div>
        <div class="item-side">
          <span class="muted">ISBN: {{ b.isbn || '—' }}</span>
          <span>{{ b.publicationDate ? (b.publicationDate | date:'dd/MM/yyyy') : '—' }}</span>
        </div>
      </div>
      <div *ngIf="!data.publishedBooks?.length" class="empty">Nenhum livro publicado.</div>

      <h3>Compras</h3>
      <div *ngFor="let p of (data.purchases || []); trackBy: trackPurchase" class="item-row">
        <div class="item-main">
          <strong>{{ p.bookTitle || 'Compra #' + p.idKitPayment }}</strong>
          <span class="muted">Qtd: {{ p.quantity }}</span>
        </div>
        <div class="item-side">
          <span>{{ p.priceUnit | currency:'BRL':'symbol':'1.2-2' }}</span>
          <span class="muted">Comprado em {{ p.purchaseDate | date:'dd/MM/yyyy HH:mm' }}</span>
        </div>
      </div>
      <div *ngIf="!data.purchases?.length" class="empty">Nenhuma compra encontrada.</div>
    </mat-dialog-content>

    <mat-dialog-actions class="dlg-actions">
      <button mat-stroked-button (click)="close()">Fechar</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dlg-title { font-weight: 800; }
    .dlg { max-height: 65vh; }
    h3 {
      margin: 18px 0 8px;
      font-size: 1rem;
      font-weight: 700;
      color: #333;
      border-bottom: 1px solid #f0f0f0;
      padding-bottom: 6px;
    }
    .meta {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px 16px;
      font-size: .93rem;
      color: #444;
    }
    .item-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      padding: 10px 4px;
      border-bottom: 1px solid #f6f6f6;
      font-size: .93rem;
    }
    .item-main { display: flex; flex-direction: column; gap: 2px; }
    .item-side { display: flex; flex-direction: column; gap: 2px; text-align: right; }
    .muted { color: #7a7f87; font-size: .87rem; }
    .empty { color: #6f7680; padding: 8px 4px; font-size: .92rem; }
    .dlg-actions {
      justify-content: flex-end;
      gap: 10px;
      padding: 12px 24px 16px;
    }
    @media (max-width: 600px) {
      .meta { grid-template-columns: 1fr; }
      .item-row { flex-direction: column; align-items: flex-start; }
      .item-side { text-align: left; }
    }
  `]
})
export class AdminUserDetailsDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AdminUserDetailsResponse,
    private ref: MatDialogRef<AdminUserDetailsDialogComponent>,
  ) { }

  close() { this.ref.close(); }

  formatAddress(): string {
    const u = this.data.user;
    const parts = [
      u.street,
      u.addressNumber,
      u.complement,
      u.neighborhood,
      u.city && u.state ? `${u.city}/${u.state}` : (u.city || u.state),
      u.zipcode,
    ].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }

  bookTypeLabel(): string { return publicationProfileLabel(PUBLICATION_BOOK_TYPE_OPTIONS, this.data.user.publicationBookType); }
  textReadyLabel(): string { return publicationProfileLabel(PUBLICATION_TEXT_READY_OPTIONS, this.data.user.publicationTextReady); }
  timeframeLabel(): string { return publicationProfileLabel(PUBLICATION_TIMEFRAME_OPTIONS, this.data.user.publicationTimeframe); }
  budgetLabel(): string { return publicationProfileLabel(PUBLICATION_BUDGET_OPTIONS, this.data.user.publicationBudget); }

  trackBook(index: number, b: AdminUserPublishedBook): number { return b.idBookVersion; }
  trackPurchase(index: number, p: AdminUserPurchase): number { return p.idKitPaymentItem; }
}
